import React, { useState, useEffect, useRef, useCallback } from 'react';
import "react-toastify/dist/ReactToastify.css";
import './App.css';
import Calendar from './components/Calendar/Calendar';
import ImageUploader from './components/ImageUploader';
import MonthGrid from './components/MonthGrid';
import Header from './components/Header/Header';
import MainVisual from './components/MainVisual/MainVisual';
import About from './components/About/About';
import OptionSelect from './components/OptionSelect';
import FAQ from './components/FAQ/FAQ';
import Footer from './components/Footer/Footer';
import Billing from './components/Billing';
import { ToastContainer } from 'react-toastify';
import Download from './components/Download/Download';

const desktopBuildSteps = [
  { id: 'step01', indexLabel: 'step01', title: '写真を選ぶ' },
  { id: 'step02', indexLabel: 'step02', title: '手書き数字' },
  { id: 'step03', indexLabel: 'step03', title: 'プレビュー' },
  { id: 'option', indexLabel: 'option', title: 'オプション' },
];

function App() {
  const [images, setImages] = useState({});
  const [additionalImages, ] = useState({});
  const [, setRecognizedDigits] = useState([]);
  const [positions, ] = useState({});
  const [monthImages, setMonthImages] = useState({});
  const [startMonth, setStartMonth] = useState(0);
  const [startDay, setStartDay] = useState(0);
  const [language, setLanguage] = useState('英語');
  const [selectedFrame, setSelectedFrame] = useState(0);
  const [framePrice, setFramePrice] = useState(0);
  const [couponInfo, setCouponInfo] = useState(null);
  const [isBeforeStepOne, setIsBeforeStepOne] = useState(true);
  const [isDesktopBuild, setIsDesktopBuild] = useState(() => window.innerWidth >= 768);
  const [activeDesktopStep, setActiveDesktopStep] = useState(0);
  const calendarRef = useRef(null);
  const desktopBuildRef = useRef(null);

  const onOptionsChange = useCallback((options) => {
    const framePrices = OptionSelect.frames.map(frame => frame.price);
    setFramePrice(framePrices[options.selectedFrame]);
    setStartDay(options.startDay); // 追加
  }, []);

  useEffect(() => {
    const framePrices = OptionSelect.frames.map(frame => frame.price);
    setFramePrice(framePrices[selectedFrame]);
  }, [selectedFrame]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleViewportChange = (event) => {
      setIsDesktopBuild(event.matches);
    };

    setIsDesktopBuild(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (isDesktopBuild) {
      setIsBeforeStepOne(true);
      return undefined;
    }

    const updateStepPosition = () => {
      const stepOneSection = document.getElementById('month-grid');

      if (!stepOneSection) {
        setIsBeforeStepOne(true);
        return;
      }

      const stepOneTop = stepOneSection.getBoundingClientRect().top + window.scrollY;
      const floatingButtonOffset = 80;
      setIsBeforeStepOne(window.scrollY + floatingButtonOffset < stepOneTop);
    };

    updateStepPosition();
    window.addEventListener('scroll', updateStepPosition, { passive: true });
    window.addEventListener('resize', updateStepPosition);

    return () => {
      window.removeEventListener('scroll', updateStepPosition);
      window.removeEventListener('resize', updateStepPosition);
    };
  }, [isDesktopBuild]);

  const handleRecognitionComplete = (result) => {
    setRecognizedDigits(result.digits);
    setImages(result.images || {});
  };

  const scrollDesktopBuildIntoView = useCallback(() => {
    if (!desktopBuildRef.current) {
      return;
    }

    const top = desktopBuildRef.current.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }, []);

  const handleDesktopStepChange = useCallback((nextStepIndex) => {
    setActiveDesktopStep(nextStepIndex);
    window.requestAnimationFrame(() => {
      scrollDesktopBuildIntoView();
    });
  }, [scrollDesktopBuildIntoView]);

  const finishImageSection = (
    <div className="finish_image">
      <div className="finish_image-panel">
        <p className="month-grid-step">step03</p>
        <h3 className="finish_image-title">完成イメージ</h3>
        <div ref={calendarRef} className="finish_image-preview">
          <Calendar
            images={{ ...images, ...additionalImages }}
            positions={positions}
            monthImages={monthImages}
            startMonth={startMonth}
            startDay={startDay}
            language={language}
            selectedFrame={selectedFrame}
          />
        </div>
        <ul className="finish_image-notes">
          <li>実際に印刷する場合と色味が異なる場合があります。</li>
          <li>写真の解像度が低いと、印刷した際に写真が荒く見えることがあります。</li>
          <li>ハガキサイズのPDFをダウンロードすることができます。</li>
        </ul>
      </div>
    </div>
  );

  const buildSections = [
    <MonthGrid key="month-grid" monthImages={monthImages} setMonthImages={setMonthImages} />,
    <ImageUploader key="image-uploader" onRecognitionComplete={handleRecognitionComplete} />,
    <div key="finish-image">{finishImageSection}</div>,
    <OptionSelect
      key="option-select"
      startMonth={parseInt(startMonth, 10)}
      setStartMonth={setStartMonth}
      startDay={startDay}
      setStartDay={setStartDay}
      language={language}
      setLanguage={setLanguage}
      selectedFrame={selectedFrame}
      setSelectedFrame={setSelectedFrame}
      onOptionsChange={onOptionsChange}
      forceOpen={isDesktopBuild}
    />,
  ];

  const isPaymentVisible = !isDesktopBuild || activeDesktopStep === desktopBuildSteps.length - 1;

  return (
    <div className="container">
      <ToastContainer
        autoClose={3000}
      />
      <Header hideMobileCta={!isBeforeStepOne} />
      <MainVisual />
      <About />

      {isDesktopBuild ? (
        <section ref={desktopBuildRef} className="desktop-build-flow">
          <div className="desktop-build-flow-inner">
            <div className="desktop-build-stepper" aria-label="作成ステップ">
              {desktopBuildSteps.map((step, index) => {
                const isActive = index === activeDesktopStep;
                const isCompleted = index < activeDesktopStep;

                return (
                  <div key={step.id} className="desktop-build-stepper-node">
                    <button
                      type="button"
                      className={`desktop-build-stepper-item${isActive ? ' is-active' : ''}${isCompleted ? ' is-completed' : ''}`}
                      onClick={() => handleDesktopStepChange(index)}
                      aria-current={isActive ? 'step' : undefined}
                    >
                      <span className="desktop-build-stepper-index">{step.indexLabel}</span>
                      <span className="desktop-build-stepper-title">{step.title}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="desktop-build-stage">
              {buildSections.map((section, index) => (
                <div
                  key={desktopBuildSteps[index].id}
                  className={`desktop-build-stage-panel${index === activeDesktopStep ? ' is-active' : ''}`}
                  aria-hidden={index !== activeDesktopStep}
                >
                  <div className="desktop-step-view">
                    {section}
                  </div>
                </div>
              ))}
            </div>

            <div className={`desktop-build-nav${activeDesktopStep === 0 ? ' is-first-step' : ''}`}>
              {activeDesktopStep > 0 && (
                <button
                  type="button"
                  className="desktop-build-nav-button"
                  onClick={() => handleDesktopStepChange(activeDesktopStep - 1)}
                >
                  戻る
                </button>
              )}
              {activeDesktopStep < desktopBuildSteps.length - 1 && (
                <button
                  type="button"
                  className="desktop-build-nav-button"
                  onClick={() => handleDesktopStepChange(activeDesktopStep + 1)}
                >
                  次へ
                </button>
              )}
            </div>
          </div>
        </section>
      ) : (
        <>
          {buildSections}
        </>
      )}

      {isPaymentVisible && (
        <section className="payment-section">
          <div className="payment-section-inner">
            <Billing framePrice={framePrice} couponInfo={couponInfo} />

            <Download calendarRef={calendarRef} framePrice={framePrice} couponInfo={couponInfo} onCouponApply={setCouponInfo} />
          </div>
        </section>
      )}

      <FAQ />
      <Footer />
    </div>
  );
}

export default App;
